import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ArrowLeft,
  Package,
  CreditCard,
  User,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import { apiFetch } from '@/lib/operator-api';
import type { OrderStatus, OrderPaymentStatus } from '@/types/orders';

export const Route = createFileRoute('/_auth/operator/orders/$id')({
  component: OrderDetailPage,
});

interface OrderDetail {
  id: string;
  orderNumber: string;
  businessId: string;
  customerId?: string;
  customerName: string;
  status: OrderStatus;
  paymentStatus: OrderPaymentStatus;
  subtotal: number;
  total: number;
  taxAmount: number;
  discountAmount: number;
  tipAmount: number;
  notes?: string;
  createdAt: string;
  updatedAt?: string;
  processingAt?: string;
  readyAt?: string;
  completedAt?: string;
  refundedAt?: string;
  customer?: { id: string; name: string | null; phoneNumberE164: string };
  business?: { id: string; name: string };
  lineItems: Array<{
    id: string;
    productName: string;
    unitPrice: number;
    quantity: number;
    totalPrice: number;
    serviceId?: string;
  }>;
}

const VALID_STATUS_TRANSITIONS: Record<string, OrderStatus[]> = {
  PENDING: ['PROCESSING', 'CANCELLED'],
  PROCESSING: ['READY', 'CANCELLED'],
  READY: ['COMPLETED', 'CANCELLED'],
  COMPLETED: [],
  CANCELLED: [],
};

function getStatusBadgeVariant(
  status: string
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'PENDING':
      return 'secondary';
    case 'PROCESSING':
      return 'default';
    case 'READY':
      return 'default';
    case 'COMPLETED':
      return 'default';
    case 'CANCELLED':
    case 'REFUNDED':
      return 'destructive';
    case 'PAID':
      return 'default';
    case 'UNPAID':
      return 'secondary';
    case 'PARTIALLY_PAID':
      return 'outline';
    default:
      return 'secondary';
  }
}

function formatCents(cents: number): string {
  return `R$ ${(Number(cents) / 100).toFixed(2)}`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function OrderDetailPage() {
  const { id } = Route.useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchOrder = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/operator/orders/${id}`, { signal });
      if (signal?.aborted) return;
      if (!res.ok) {
        throw new Error(`Failed to fetch order: ${res.status}`);
      }
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Failed to fetch order');
      setOrder(json.data);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    const controller = new AbortController();
    fetchOrder(controller.signal);
    return () => controller.abort();
  }, [fetchOrder]);

  const handleStatusUpdate = async (newStatus: OrderStatus) => {
    if (!order) return;
    setActionLoading(true);
    try {
      const res = await apiFetch(`/api/operator/orders/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Failed to update');
      setOrder(json.data);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update order');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePaymentUpdate = async (paymentStatus: OrderPaymentStatus) => {
    if (!order) return;
    setActionLoading(true);
    try {
      const res = await apiFetch(`/api/operator/orders/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ paymentStatus }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Failed to update');
      setOrder(json.data);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update payment');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 space-y-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate({ to: '/operator/orders' })}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('common.back')}
        </Button>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              <p>{error}</p>
            </div>
            <Button variant="outline" className="mt-4" onClick={() => fetchOrder()}>
              {t('orders.retry')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!order) return null;

  const nextStatuses = VALID_STATUS_TRANSITIONS[order.status] || [];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate({ to: '/operator/orders' })}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.back')}
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              {order.orderNumber || `Order #${id.slice(-8)}`}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t('orders.orderDetails')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={getStatusBadgeVariant(order.status)}>
            {order.status}
          </Badge>
          <Badge variant={getStatusBadgeVariant(order.paymentStatus)}>
            {order.paymentStatus}
          </Badge>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {/* Left column: Order info + Line items */}
        <div className="md:col-span-2 space-y-6">
          {/* Line Items */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                {t('orders.lineItems')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('orders.item')}</TableHead>
                    <TableHead className="text-right">
                      {t('orders.unitPrice')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('orders.quantity')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('orders.total')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {order.lineItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">
                        {item.productName}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCents(item.unitPrice)}
                      </TableCell>
                      <TableCell className="text-right">
                        {item.quantity}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCents(item.totalPrice)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Totals */}
              <div className="border-t px-4 py-3 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {t('orders.subtotal')}
                  </span>
                  <span>{formatCents(order.subtotal)}</span>
                </div>
                {Number(order.taxAmount) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      {t('orders.tax')}
                    </span>
                    <span>{formatCents(order.taxAmount)}</span>
                  </div>
                )}
                {Number(order.discountAmount) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      {t('orders.discount')}
                    </span>
                    <span>-{formatCents(order.discountAmount)}</span>
                  </div>
                )}
                {Number(order.tipAmount) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      {t('orders.tip')}
                    </span>
                    <span>{formatCents(order.tipAmount)}</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between font-semibold text-lg">
                  <span>{t('orders.total')}</span>
                  <span>{formatCents(order.total)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          {order.notes && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">
                  {t('orders.notes')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{order.notes}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right column: Customer, Actions, Timeline */}
        <div className="space-y-6">
          {/* Customer */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                {t('orders.customer')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p className="font-medium">
                  {order.customer?.name || order.customerName}
                </p>
                {order.customer?.phoneNumberE164 && (
                  <p className="text-sm text-muted-foreground">
                    {order.customer.phoneNumberE164}
                  </p>
                )}
                {order.customerId && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-2"
                    onClick={() =>
                      navigate({
                        to: `/operator/customers/${order.customerId}`,
                      })
                    }
                  >
                    {t('orders.viewCustomer')}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Business */}
          {order.business && (
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">
                  {t('orders.business')}
                </p>
                <p className="font-medium">{order.business.name}</p>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">
                {t('orders.actions')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Status transitions */}
              {nextStatuses.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    {t('orders.updateStatus')}
                  </p>
                  {nextStatuses.map((status) => (
                    <Button
                      key={status}
                      variant={
                        status === 'CANCELLED' ? 'destructive' : 'default'
                      }
                      size="sm"
                      className="w-full"
                      disabled={actionLoading}
                      onClick={() => handleStatusUpdate(status)}
                    >
                      {actionLoading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : status === 'CANCELLED' ? (
                        <XCircle className="h-4 w-4 mr-2" />
                      ) : (
                        <CheckCircle className="h-4 w-4 mr-2" />
                      )}
                      {status === 'PROCESSING'
                        ? t('orders.startProcessing')
                        : status === 'READY'
                          ? t('orders.markReady')
                          : status === 'COMPLETED'
                            ? t('orders.markComplete')
                            : status === 'CANCELLED'
                              ? t('orders.cancel')
                              : status}
                    </Button>
                  ))}
                </div>
              )}

              <Separator />

              {/* Payment actions */}
              {order.paymentStatus !== 'PAID' &&
                order.status !== 'CANCELLED' && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">
                      {t('orders.payment')}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      disabled={actionLoading}
                      onClick={() => handlePaymentUpdate('PAID')}
                    >
                      <CreditCard className="h-4 w-4 mr-2" />
                      {t('orders.markPaid')}
                    </Button>
                  </div>
                )}
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                {t('orders.timeline')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <TimelineEntry
                  label={t('orders.created')}
                  date={order.createdAt}
                />
                {order.processingAt && (
                  <TimelineEntry
                    label={t('orders.processing')}
                    date={order.processingAt}
                  />
                )}
                {order.readyAt && (
                  <TimelineEntry
                    label={t('orders.ready')}
                    date={order.readyAt}
                  />
                )}
                {order.completedAt && (
                  <TimelineEntry
                    label={t('orders.completed')}
                    date={order.completedAt}
                  />
                )}
                {order.refundedAt && (
                  <TimelineEntry
                    label={t('orders.refunded')}
                    date={order.refundedAt}
                  />
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function TimelineEntry({ label, date }: { label: string; date: string }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span>{formatDate(date)}</span>
    </div>
  );
}
